import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  EVENT_QUEUE_DURATION_MS,
  houseLimits,
  isEventQueueMode,
  setHouseLimits,
  type EventQueueLivePublic,
} from '@shared/index'
import { api } from '../api/http'
import { hydrateAccount } from '../account/sync'
import { formatEventCountdown } from '../game/eventCountdown'

export type MaintenanceLevel = 'off' | 'closed' | 'full'

interface SiteStatus {
  maintenance: MaintenanceLevel
  resetDone: string[]
  houseLimits: Record<string, number>
  eventQueue?: EventQueueLivePublic | null
}

export const useSiteStore = defineStore('site', () => {
  const maintenance = ref<MaintenanceLevel>('off')
  const resetDone = ref<string[]>([])
  const limits = ref<Record<string, number>>({ ...houseLimits() })

  const eventQueue = ref<EventQueueLivePublic | null>(null)
  const now = ref(Date.now())
  let clock: ReturnType<typeof setInterval> | null = null

  function armClock(): void {
    if (clock) return
    clock = setInterval(() => {
      now.value = Date.now()
      if (!eventQueue.value) {
        if (clock) clearInterval(clock)
        clock = null
      }
    }, 1000)
  }

  function setEventQueue(next: EventQueueLivePublic | null): void {
    eventQueue.value = next
    if (next) armClock()
  }

  const eventCountdown = computed(() => {
    const live = eventQueue.value
    if (!live) return ''
    return formatEventCountdown(live.endsAt, now.value)
  })
  async function refresh(): Promise<void> {
    // Timer poll. A slow phone would flash the menu spinner on every cycle.
    const body = await api<SiteStatus>('/site/status', { quiet: true })
    if (body.maintenance !== 'off' && body.maintenance !== 'closed' && body.maintenance !== 'full') return
    const prev = maintenance.value
    maintenance.value = body.maintenance
    resetDone.value = Array.isArray(body.resetDone) ? body.resetDone : []
    limits.value = body.houseLimits ?? {}
    setHouseLimits(limits.value)
    setEventQueue(readEventQueue(body.eventQueue))
    if (prev !== 'off' && maintenance.value === 'off') {
      void hydrateAccount(true)
    }
  }

  function applyPush(body: {
    maintenance?: MaintenanceLevel
    eventQueue?: EventQueueLivePublic | null
  }): void {
    if (body.maintenance === 'closed' || body.maintenance === 'full' || body.maintenance === 'off') {
      const prev = maintenance.value
      maintenance.value = body.maintenance
      if (prev !== 'off' && maintenance.value === 'off') void hydrateAccount(true)
    }
    if (body.eventQueue !== undefined) setEventQueue(readEventQueue(body.eventQueue))
    void refresh()
  }

  return {
    maintenance,
    resetDone,
    houseLimits: limits,
    eventQueue,
    eventCountdown,
    refresh,
    applyPush,
  }
})

function readEventQueue(raw: unknown): EventQueueLivePublic | null {
  if (!raw || typeof raw !== 'object') return null
  const rec = raw as { mode?: unknown; startsAt?: unknown; endsAt?: unknown }
  if (!isEventQueueMode(rec.mode) || typeof rec.endsAt !== 'string') return null
  const startsAt =
    typeof rec.startsAt === 'string' && rec.startsAt
      ? rec.startsAt
      : new Date(Date.parse(rec.endsAt) - EVENT_QUEUE_DURATION_MS).toISOString()
  if (!Number.isFinite(Date.parse(startsAt))) return null
  return { mode: rec.mode, startsAt, endsAt: rec.endsAt }
}